import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs, { breadcrumbSchema } from "@/components/Breadcrumbs";
import { KineticLines, SectionHead } from "@/components/Motion";
import { ProductCard } from "@/components/Cards";
import CTASection from "@/components/CTASection";
import NotFound from "@/pages/NotFound";
import { SITE } from "@/config/site";
import api from "@/lib/api";

export default function KnowledgeArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/knowledge/${slug}`).then((r) => setArticle(r.data))
      .catch(() => setArticle(null)).finally(() => setLoading(false));
  }, [slug]);

  if (!loading && !article) return <NotFound />;
  if (!article) return <div className="min-h-[60vh]" />;

  const crumbs = [{ label: "Knowledge Center", to: "/knowledge-center" }, { label: article.title }];
  const jsonLd = [
    breadcrumbSchema(crumbs),
    {
      "@context": "https://schema.org", "@type": "Article",
      headline: article.title, description: article.excerpt,
      author: { "@type": "Organization", name: article.author || SITE.brand },
      publisher: { "@type": "Organization", name: SITE.legalName },
      datePublished: article.date,
    },
  ];

  return (
    <div data-testid="knowledge-article-page">
      <SEO title={article.title} path={`/knowledge-center/${slug}`} description={article.excerpt} type="article" jsonLd={jsonLd} />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <Breadcrumbs dark items={crumbs} />
          <p className="eyebrow eyebrow-light">{article.topic}</p>
          <KineticLines lines={[article.title]}
            className="mt-4 font-display text-3xl font-medium leading-[1.12] tracking-tight text-white sm:text-4xl" />
          <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
            {article.author} · {article.date}
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
        <p className="text-xl font-light leading-relaxed text-ink">{article.excerpt}</p>
        {article.content?.map((para, i) => (
          <p key={i} className="mt-6 text-base leading-[1.85] text-steel" data-testid={`kc-para-${i}`}>{para}</p>
        ))}
        {article.tags?.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="border border-line bg-mist px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-steel">{t}</span>
            ))}
          </div>
        )}
      </article>

      {article.products?.length > 0 && (
        <section className="border-t border-line bg-mist">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <SectionHead eyebrow="Related Equipment" title="STJ Equipment Covered in This Topic" />
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="kc-related-products">
              {article.products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {article.related_articles?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Continue Reading" title="Related Topics" />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {article.related_articles.map((a) => (
              <Link key={a.slug} to={`/knowledge-center/${a.slug}`} data-testid={`kc-related-${a.slug}`}
                className="group border border-line bg-white p-6 transition-colors hover:border-accent/40">
                <h3 className="font-display text-base font-semibold leading-snug text-ink group-hover:text-accent">{a.title}</h3>
                <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-accent">
                  Read <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
      <CTASection />
    </div>
  );
}
